import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X, Upload } from "lucide-react";
import { campaignAPI, memberAPI } from "../services/api";
import * as XLSX from "xlsx";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 400;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const CompanyDropdownWrapper = styled.div`
  position: relative;
`;

const CompanyDropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
    color: #9ca3af;
  }
`;

const ChevronIcon = styled.span`
  font-size: 0.75rem;
  transition: transform 0.2s;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0deg)")};
`;

const CompanyDropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 220px;
  overflow-y: auto;
`;

const CompanyCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;

  &:hover {
    background-color: #f0fffe;
  }

  input[type="checkbox"] {
    accent-color: #00b5b8;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
`;

const SelectedTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.75rem;
`;

const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: 1px solid #00b5b8;
  color: #00b5b8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.5rem 0.5rem 0.125rem 0.125rem;
  font-size: 0.75rem;
  min-height: 30px;
  height: auto;
  width: auto;
  position: relative;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00b5b8;
  cursor: pointer;
  color: #00b5b8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;

  &:hover {
    background: #f0fffe;
  }
`;
const UploadDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const UploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 0.375rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;

  &:hover {
    border-color: #00b5b8;
    background-color: #f0fffe;
  }
`;

const UploadIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
  color: #9ca3af;
`;

const UploadText = styled.p`
  color: #374151;
  font-size: 0.875rem;
  margin: 0;
`;

const UploadLink = styled.span`
  color: #00b5b8;
  text-decoration: underline;
  cursor: pointer;
`;

const UploadInput = styled.input`
  display: none;
`;

const DownloadLink = styled.a`
  display: inline-block;
  color: #00b5b8;
  font-size: 0.875rem;
  text-decoration: underline;
  margin-top: 0.5rem;
  cursor: pointer;

  &:hover {
    color: #0891b2;
  }
`;

const FileName = styled.p`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }

  &:disabled {
    background-color: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background-color: #00b5b8;
  color: white;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #0891b2;
  }

  &:disabled {
    background-color: #d1d5db;
    cursor: not-allowed;
  }
`;

const ProgressContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f3f4f6;
  border-radius: 0.375rem;
`;

const ProgressText = styled.p`
  font-size: 0.875rem;
  color: #374151;
  margin: 0.25rem 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: #00b5b8;
  transition: width 0.3s ease;
  width: ${(props) => props.$progress}%;
`;

const ResultContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: ${(props) => (props.$hasError ? "#fef2f2" : "#f0fdf4")};
  border: 1px solid ${(props) => (props.$hasError ? "#fecaca" : "#bbf7d0")};
  border-radius: 0.375rem;
`;

const ResultTitle = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: ${(props) => (props.$hasError ? "#dc2626" : "#16a34a")};
  margin: 0 0 0.75rem 0;
`;

const ResultStats = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0.75rem;
`;

const ResultStat = styled.div`
  font-size: 0.875rem;
  color: #374151;
`;

const ErrorListContainer = styled.div`
  margin-top: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  padding: 0.75rem;
`;

const ErrorListTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const ErrorItem = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0.25rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const GroupingModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    organization: "",
    file: null,
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    success: 0,
    failed: 0,
  });
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await campaignAPI.getList(1, "");
      if (response.result && response.list && Array.isArray(response.list)) {
        const uniqueCompanies = response.list.filter(
          (item) => item.name && item.name !== "-"
        );
        setCompanies(uniqueCompanies);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file });
    }
  };

  const handleUploadClick = () => {
    document.getElementById("file-upload-input").click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.organization) {
      alert("กรุณาเลือกบริษัท");
      return;
    }

    if (!formData.file) {
      alert("กรุณาอัปโหลดไฟล์");
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: 0, success: 0, failed: 0 });

    try {
      const data = await readFile(formData.file);

      if (!data || data.length === 0) {
        alert("ไฟล์ไม่มีข้อมูล");
        setUploading(false);
        return;
      }

      setUploadProgress((prev) => ({ ...prev, total: data.length }));

      let successCount = 0;
      let failedCount = 0;
      let updatedCount = 0;
      const errorList = [];

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNumber = i + 1;

        try {
          const nidKey = Object.keys(row).find(
            (key) =>
              key.includes("เลขบัตร") ||
              key.includes("บัตรประชาชน") ||
              key.toLowerCase().includes("nid") ||
              key.toLowerCase().includes("id card")
          );

          const deptKey = Object.keys(row).find(
            (key) =>
              key.includes("แผนก") ||
              key.toLowerCase().includes("department") ||
              key.toLowerCase().includes("dept")
          );

          const posKey = Object.keys(row).find(
            (key) =>
              key.includes("ตำแหน่ง") ||
              key.toLowerCase().includes("position") ||
              key.toLowerCase().includes("job")
          );

          const phoneKey = Object.keys(row).find(
            (key) =>
              key.includes("เบอร์โทร") ||
              key.includes("โทรศัพท์") ||
              key.includes("เบอร์") ||
              key.toLowerCase().includes("phone") ||
              key.toLowerCase().includes("tel") ||
              key.toLowerCase().includes("mobile")
          );

          let nid = nidKey ? String(row[nidKey]).trim() : "";
          const department = deptKey ? String(row[deptKey]).trim() : "";
          const jobPosition = posKey ? String(row[posKey]).trim() : "";
          let phone = phoneKey ? String(row[phoneKey]).trim() : "";

          if (!nid && Object.keys(row).length > 0) {
            const firstKey = Object.keys(row)[0];
            nid = String(row[firstKey]).trim();
          }

          if (nid && nid.startsWith("'")) {
            nid = nid.substring(1);
          }

          if (nid && nid.includes("E+")) {
            nid = parseFloat(nid).toFixed(0);
          }

          if (phone) {
            phone = phone.replace(/[-\s]/g, "");
            if (phone.includes("E+")) {
              phone = parseFloat(phone).toFixed(0);
            }
          }

          if (!nid || nid === "" || nid === "0") {
            failedCount++;
            errorList.push(`แถวที่ ${rowNumber} : ไม่มีเลขบัตรประชาชน`);
            continue;
          }

          let existingMember = null;
          try {
            const searchResponse = await memberAPI.getList({ keywordName: nid, pageNow: 1 });
            if (searchResponse.result && searchResponse.list && searchResponse.list.length > 0) {
              existingMember = searchResponse.list.find((m) => {
                const mNid = String(m.nid || m.idCard || m.idcard || "").trim();
                return mNid === nid;
              }) || searchResponse.list[0];
            }
          } catch (searchErr) {
            console.error(`Error searching NID ${nid}:`, searchErr);
          }

          if (existingMember) {
            const memberData = {
              ...existingMember,
              username: nid,
              campaignid: formData.organization,
              position: department || existingMember.position || "",
              job: jobPosition || existingMember.job || "",
              tel: phone || existingMember.tel || "",
            };

            await memberAPI.update(memberData);
            updatedCount++;
            successCount++;
          } else {
            failedCount++;
            errorList.push(`แถวที่ ${rowNumber} : ไม่พบเลขบัตร ${nid} ในระบบ`);
          }
        } catch (err) {
          failedCount++;
          errorList.push(
            `แถวที่ ${rowNumber} : เกิดข้อผิดพลาด - ${err.message}`
          );
        }

        setUploadProgress({
          current: i + 1,
          total: data.length,
          success: successCount,
          failed: failedCount,
        });
      }

      setUploadResult({
        updatedCount,
        failedCount,
        totalCount: data.length,
        errorList,
      });

      if (onSave) {
        onSave({
          success: successCount,
          failed: failedCount,
          total: data.length,
        });
      }
    } catch (err) {
      console.error("Error processing file:", err);
    } finally {
      setUploading(false);
    }
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const buffer = new Uint8Array(e.target.result);

          const workbook = XLSX.read(buffer, {
            type: "array",
            codepage: 874,
            cellText: true,
            cellDates: false,
          });

          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          const range = XLSX.utils.decode_range(worksheet["!ref"]);
          const headers = [];
          const rows = [];

          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
            const cell = worksheet[cellAddress];
            headers.push(cell ? cell.v : "");
          }

          for (let R = range.s.r + 1; R <= range.e.r; ++R) {
            const row = {};
            let hasData = false;

            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
              const cell = worksheet[cellAddress];
              const header = headers[C];

              if (cell && cell.v !== undefined && cell.v !== null && cell.v !== "") {
                hasData = true;

                if (
                  header &&
                  (header.includes("เลขบัตร") ||
                    header.includes("บัตรประชาชน") ||
                    header.toLowerCase().includes("nid") ||
                    header.toLowerCase().includes("id card"))
                ) {
                  row[header] =
                    cell.w ||
                    (typeof cell.v === "number" ? cell.v.toString() : cell.v);
                } else {
                  row[header] = cell.v;
                }
              } else {
                row[header] = "";
              }
            }

            if (hasData) {
              rows.push(row);
            }
          }

          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleClose = () => {
    setFormData({
      organization: "",
      file: null,
    });
    setUploadResult(null);
    setUploadProgress({ current: 0, total: 0, success: 0, failed: 0 });
    onClose();
  };

  const handleDownloadExample = () => {
    const data = [
      {
        เลขบัตรประชาชน: "0000000000000",
        รหัสบริษัท: "707",
        "แผนก (Department)": "ฝ่ายบุคคล",
        "ตำแหน่งงาน (JOB)": "เจ้าหน้าที่",
        เบอร์โทรศัพท์มือถือ: "0000000000",
      },
      {
        เลขบัตรประชาชน: "0000000000000",
        รหัสบริษัท: "707",
        "แผนก (Department)": "ฝ่ายการตลาด",
        "ตำแหน่งงาน (JOB)": "ผู้ช่วยผู้จัดการ",
        เบอร์โทรศัพท์มือถือ: "0000000000",
      },
      {
        เลขบัตรประชาชน: "0000000000000",
        รหัสบริษัท: "707",
        "แผนก (Department)": "ฝ่ายไอที",
        "ตำแหน่งงาน (JOB)": "นักพัฒนาระบบ",
        เบอร์โทรศัพท์มือถือ: "0000000000",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(data);

    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 15 },
      { wch: 25 },
      { wch: 25 },
      { wch: 20 },
    ];

    // กำหนดให้คอลัมน์ A (เลขบัตรประชาชน) เป็น Text format เพื่อป้องกัน scientific notation
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].t = "s";
        worksheet[cellAddress].z = "@";
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ตัวอย่างข้อมูล");

    XLSX.writeFile(workbook, "exmemberv1.xlsx");
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>จัดกลุ่มผู้รับการตรวจ</ModalTitle>
          <CloseButton onClick={handleClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>เลือกบริษัทที่ต้องการนำเข้าผู้รับการตรวจสุขภาพ</Label>
              <SelectInput
                value={formData.organization}
                onChange={(e) =>
                  setFormData({ ...formData, organization: e.target.value })
                }
                disabled={loading}
              >
                <option value="">
                  {loading ? "กำลังโหลด..." : "เลือกบริษัท"}
                </option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name} (รหัสบริษัท: {company.id})
                  </option>
                ))}
              </SelectInput>
            </FormGroup>

            <FormGroup>
              <UploadDescription>
                นำเข้าไฟล์ excel, csv โปรดระบุบัตรประชาชน, รหัสบริษัท, แผนก,
                ตำแหน่งงาน
              </UploadDescription>

              <UploadInput
                id="file-upload-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
              />

              <UploadArea onClick={handleUploadClick}>
                <UploadIcon>
                  <Upload size={32} />
                </UploadIcon>
                <UploadText>
                  <UploadLink>upload file</UploadLink> คลิก
                </UploadText>
              </UploadArea>

              {formData.file && (
                <FileName>ไฟล์ที่เลือก: {formData.file.name}</FileName>
              )}

              <DownloadLink onClick={handleDownloadExample}>
                ดาวน์โหลดตัวอย่างไฟล์ v1
              </DownloadLink>
            </FormGroup>

            {uploading && (
              <ProgressContainer>
                <ProgressText>
                  กำลังนำเข้าข้อมูล: {uploadProgress.current} /{" "}
                  {uploadProgress.total}
                </ProgressText>
                <ProgressText>
                  สำเร็จ: {uploadProgress.success} | ล้มเหลว:{" "}
                  {uploadProgress.failed}
                </ProgressText>
                <ProgressBar>
                  <ProgressBarFill
                    $progress={
                      uploadProgress.total > 0
                        ? (uploadProgress.current / uploadProgress.total) * 100
                        : 0
                    }
                  />
                </ProgressBar>
              </ProgressContainer>
            )}

            {uploadResult && (
              <ResultContainer $hasError={uploadResult.failedCount > 0}>
                <ResultTitle $hasError={uploadResult.failedCount > 0}>
                  {uploadResult.failedCount > 0
                    ? "อัปเดตข้อมูลเสร็จสิ้น (มีข้อผิดพลาด)"
                    : "อัปเดตข้อมูลสำเร็จ"}
                </ResultTitle>
                <ResultStats>
                  <ResultStat>
                    อัปเดตสำเร็จ <strong>{uploadResult.updatedCount}</strong>{" "}
                    รายการ
                  </ResultStat>
                  <ResultStat>
                    ล้มเหลว <strong>{uploadResult.failedCount}</strong> รายการ
                  </ResultStat>
                  <ResultStat>
                    รวมทั้งหมด <strong>{uploadResult.totalCount}</strong> รายการ
                  </ResultStat>
                </ResultStats>

                {uploadResult.errorList &&
                  uploadResult.errorList.length > 0 && (
                    <ErrorListContainer>
                      <ErrorListTitle>
                        รายละเอียดข้อผิดพลาด ({uploadResult.errorList.length}{" "}
                        รายการ)
                      </ErrorListTitle>
                      {uploadResult.errorList
                        .slice(0, 20)
                        .map((error, index) => (
                          <ErrorItem key={index}>{error}</ErrorItem>
                        ))}
                      {uploadResult.errorList.length > 20 && (
                        <ErrorItem
                          style={{ fontStyle: "italic", color: "#9ca3af" }}
                        >
                          ... และอีก {uploadResult.errorList.length - 20} รายการ
                        </ErrorItem>
                      )}
                    </ErrorListContainer>
                  )}
              </ResultContainer>
            )}
          </ModalBody>

          <ModalFooter>
            {uploadResult ? (
              <SubmitButton type="button" onClick={handleClose}>
                ปิด
              </SubmitButton>
            ) : (
              <>
                <CancelButton
                  type="button"
                  onClick={handleClose}
                  disabled={uploading}
                >
                  ยกเลิก
                </CancelButton>
                <SubmitButton type="submit" disabled={uploading}>
                  {uploading ? "กำลังนำเข้า..." : "ตกลง"}
                </SubmitButton>
              </>
            )}
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default GroupingModal;
